import { useMemo } from 'react';
import { useUpdateEffect } from 'react-use';

import { Bean, FieldControllerProps, ValidationRules } from '@dreryk/react-common';
import { TextInput } from '@dreryk/react-components';

import { medicalServiceTranslationKeys } from '../../../../translations';
import { MedicalServiceNameForReceipt, WithNameForReceipt } from '../nameForReceipt.types';

const NAME_FOR_RECEIPT_MAX_LENGTH = 150;
const t = medicalServiceTranslationKeys.shared.nameForReceipt.textView;

type MedicalServiceNameForReceiptFieldProps = Omit<
   FieldControllerProps<
      'nameForReceipt',
      MedicalServiceNameForReceipt,
      WithNameForReceipt,
      'register' | 'watch' | 'setValue'
   >,
   'required'
> & {
   readonly disabled?: boolean;
};

export const MedicalServiceNameForReceiptField = (props: MedicalServiceNameForReceiptFieldProps): JSX.Element => {
   const { name, nameForInvoicing, nameForReceiptType } = props.watch([
      'name',
      'nameForInvoicing',
      'nameForReceiptType',
   ]);

   const disabled = props.disabled || nameForReceiptType !== 'OTHER';
   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return { maxLength: disabled ? undefined : NAME_FOR_RECEIPT_MAX_LENGTH, required: !disabled };
   }, [disabled]);

   useUpdateEffect(() => {
      switch (nameForReceiptType) {
         case 'SAME_AS_STANDARD':
            return props.setValue('nameForReceipt', name, { shouldValidate: true });
         case 'SAME_AS_FOR_INVOICING':
            return props.setValue('nameForReceipt', nameForInvoicing, { shouldValidate: true });
         default:
            return undefined;
      }
   }, [nameForInvoicing, name, nameForReceiptType]);

   return (
      <TextInput
         testId={props.name}
         controller={props.register(props.name, validationRules)}
         defaultValue={props.defaultValue}
         disabled={disabled}
         placeholder={t.placeholder[nameForReceiptType ?? 'SAME_AS_STANDARD']}
      />
   );
};
